/**
 * Operator actions (§3.6, §7.2).
 *
 * Every operator input is an `Action` appended to the run log with the sim minute it applies at.
 * Replaying the log from the scenario's initial state reproduces the run bit-identically
 * (CLAUDE.md non-negotiable #1) — so this file is the complete list of ways a human can affect
 * the simulation. If it is not here, it cannot change the outcome.
 */

import { clamp } from './constants';
import { ackAlarm, ackAll, shelveAlarm } from './alarms';
import { startUnit, stopUnit } from './units';
import type { PlantMode, SimState, SiteConfig } from './types';

export type Action =
  | { t: 'unit-start'; unit: number }
  | { t: 'unit-stop'; unit: number }
  | { t: 'unit-setpoint'; unit: number; mw: number }
  | { t: 'unit-wicket'; unit: number; y: number }
  | { t: 'unit-rake'; unit: number }
  | { t: 'gate-target'; gate: number; opening: number }
  | { t: 'gate-heater'; gate: number; on: boolean }
  | { t: 'llo-target'; llo: number; frac: number }
  | { t: 'plant-mode'; mode: PlantMode }
  | { t: 'level-target'; elevation: number; tolerance: number }
  | { t: 'discharge-target'; flow: number }
  | { t: 'mw-window'; min: number; max: number }
  | { t: 'auto-spill'; armed: boolean; maxOpening: number }
  | { t: 'schedule'; hours: number[] }
  | { t: 'agc'; enabled: boolean }
  | { t: 'alarm-ack'; id: string }
  | { t: 'alarm-ack-all' }
  | { t: 'alarm-shelve'; id: string; minutes: number }
  | { t: 'select-transducer'; which: 'a' | 'b' }
  | { t: 'request-tdg-waiver' }
  | { t: 'declare-flood-ops'; declared: boolean }
  | { t: 'declare-eap'; level: number }
  | { t: 'issue-flood-warning' }
  | { t: 'dispatch-crew'; target: string }
  | { t: 'override'; active: boolean }
  | { t: 'defer-maintenance'; unit: number }
  | { t: 'checklist'; item: string };

export interface LoggedAction {
  minute: number;
  action: Action;
}

export interface RunLog {
  seed: number;
  siteId: string;
  scenarioId: string;
  difficulty: string;
  actions: LoggedAction[];
}

/**
 * Actions that require a two-step confirm in the UI (CLAUDE.md non-negotiable #9).
 * The engine does not enforce this — the UI does — but the list lives here so there is one
 * definition and the e2e test can import it.
 */
export function requiresConfirm(action: Action, state: SimState): boolean {
  switch (action.t) {
    case 'unit-start':
    case 'unit-stop':
    case 'plant-mode':
    case 'declare-eap':
    case 'llo-target':
      return true;
    case 'gate-target': {
      const g = state.gates[action.gate];
      if (!g) return true;
      return Math.abs(action.opening - g.opening) > 1.0;
    }
    default:
      return false;
  }
}

/**
 * The tutorial checklist (Scenario 0).
 *
 * Recorded in the **engine**, from the actions themselves, rather than in the UI. That way it
 * completes identically whether the operator used the pointer, the keyboard or a replay, and it
 * cannot drift out of step with what actually happened.
 */
export const CHECKLIST_ITEMS: { id: string; label: string }[] = [
  { id: 'start-unit', label: 'Start a unit' },
  { id: 'set-setpoint', label: 'Take a unit to a MW setpoint' },
  { id: 'jog-gate', label: 'Move a spillway gate through the two-step confirm' },
  { id: 'ack-alarm', label: 'Acknowledge an alarm' },
  { id: 'level-hold', label: 'Put the plant in level hold' },
];

function tick(state: SimState, item: string): void {
  if (!state.score.checklist.includes(item)) state.score.checklist.push(item);
}

/** Apply one action. Mutates `state`. Unknown or out-of-range targets are ignored, never thrown. */
export function applyAction(site: SiteConfig, state: SimState, action: Action): void {
  switch (action.t) {
    case 'unit-start': {
      const u = state.units[action.unit];
      if (!u) return;
      if (startUnit(site, u)) {
        state.market.startCosts += site.powerhouse.startCost;
        tick(state, 'start-unit');
      }
      return;
    }
    case 'unit-stop': {
      const u = state.units[action.unit];
      if (u) stopUnit(site, u);
      return;
    }
    case 'unit-setpoint': {
      const u = state.units[action.unit];
      if (!u) return;
      u.controlMode = 'mw';
      u.setpointMW = clamp(action.mw, 0, site.powerhouse.ratedMW);
      tick(state, 'set-setpoint');
      return;
    }
    case 'unit-wicket': {
      const u = state.units[action.unit];
      if (!u) return;
      u.controlMode = 'wicket';
      u.wicketTarget = clamp(action.y, 0, 1);
      return;
    }
    case 'unit-rake': {
      const u = state.units[action.unit];
      if (u) u.rakingMinutes = site.powerhouse.rakeMinutes;
      return;
    }
    case 'gate-target': {
      const g = state.gates[action.gate];
      if (!g) return;
      g.target = clamp(action.opening, 0, site.spillway.gateHeight);
      tick(state, 'jog-gate');
      return;
    }
    case 'gate-heater': {
      const g = state.gates[action.gate];
      if (g) g.heaterOn = action.on;
      return;
    }
    case 'llo-target': {
      const l = state.llos[action.llo];
      if (l) l.targetFrac = clamp(action.frac, 0, 1);
      return;
    }
    case 'plant-mode':
      state.control.mode = action.mode;
      if (action.mode === 'level-hold') tick(state, 'level-hold');
      return;
    case 'level-target':
      state.control.levelTarget = clamp(action.elevation, site.dam.mol, site.dam.mfl);
      state.control.levelTolerance = clamp(action.tolerance, 0.05, 5);
      return;
    case 'discharge-target':
      state.control.dischargeTarget = Math.max(0, action.flow);
      return;
    case 'mw-window':
      state.control.mwMin = Math.max(0, Math.min(action.min, action.max));
      state.control.mwMax = Math.max(action.min, action.max);
      return;
    case 'auto-spill':
      state.control.autoSpillArmed = action.armed;
      state.control.autoSpillMaxOpening = clamp(action.maxOpening, 0, site.spillway.gateHeight);
      return;
    case 'schedule': {
      const hours = action.hours.slice(0, 48);
      while (hours.length < 48) hours.push(0);
      state.control.schedule = hours.map((m) =>
        clamp(m, 0, site.powerhouse.ratedMW * site.powerhouse.count),
      );
      state.control.scheduleEpoch = Math.floor(state.simMinute / 60) * 60;
      return;
    }
    case 'agc':
      state.market.agcEnabled = action.enabled;
      return;
    case 'alarm-ack':
      ackAlarm(state, action.id);
      tick(state, 'ack-alarm');
      return;
    case 'alarm-ack-all':
      if (state.alarms.some((a) => a.status === 'unacked-active')) tick(state, 'ack-alarm');
      ackAll(state);
      return;
    case 'alarm-shelve':
      shelveAlarm(state, action.id, action.minutes);
      return;
    case 'select-transducer':
      state.sensed.selected = action.which;
      return;
    case 'request-tdg-waiver': {
      if (state.tdg.waiverRequested) return;
      state.tdg.waiverRequested = true;
      // Granted after 30–90 minutes (§2.1). The delay is a hash of the request minute so it
      // stays deterministic without consuming a PRNG draw.
      const span = site.licence.waiverDelayMaxMinutes - site.licence.waiverDelayMinMinutes;
      const frac = ((state.simMinute * 2654435761) % 1000) / 1000;
      state.tdg.waiverAt = state.simMinute + site.licence.waiverDelayMinMinutes + Math.floor(frac * span);
      state.comms.push({
        id: `waiver-req-${state.simMinute}`,
        at: state.simMinute,
        from: 'Operations',
        subject: 'TDG waiver requested',
        body: 'Request submitted to the regulator for a flood-operations TDG waiver. Expect a response within 30–90 minutes.',
        actions: [],
        read: false,
      });
      return;
    }
    case 'declare-flood-ops':
      state.flags.floodOpsDeclared = action.declared;
      return;
    case 'declare-eap': {
      const level = clamp(Math.round(action.level), 0, 3);
      state.score.eapLevel = Math.max(state.score.eapLevel, level);
      state.comms.push({
        id: `eap-${state.simMinute}`,
        at: state.simMinute,
        from: 'Dam Safety',
        subject: `EAP level ${level} declared`,
        body:
          level >= 3
            ? 'Evacuation notice issued to the downstream community. Regulator and EOC notified.'
            : level === 2
              ? 'Regulator and town EOC notified. Downstream warning prepared.'
              : 'Dam safety notified; incident log opened.',
        actions: [],
        read: false,
      });
      return;
    }
    case 'issue-flood-warning': {
      if (state.downstream.floodWarningAt !== null) return;
      state.downstream.floodWarningAt = state.simMinute;
      state.comms.push({
        id: `flood-warning-${state.simMinute}`,
        at: state.simMinute,
        from: `${site.downstream.gaugeName} EOC`,
        subject: 'Flood warning issued',
        body: `Warning issued for ${site.downstream.gaugeName}. Damages are reduced 40 % if the warning precedes the breach by six hours or more.`,
        actions: [],
        read: false,
      });
      return;
    }
    case 'dispatch-crew': {
      // Halves the remaining time on any active hoist failure.
      for (const g of state.gates) {
        if (g.hoist === 'failed' && g.hoistFixedAt !== null) {
          const remaining = g.hoistFixedAt - state.simMinute;
          if (remaining > 0) g.hoistFixedAt = state.simMinute + Math.floor(remaining / 2);
        }
      }
      state.comms.push({
        id: `crew-${state.simMinute}`,
        at: state.simMinute,
        from: 'Maintenance',
        subject: 'Crew dispatched',
        body: `Crew en route to ${action.target}. Estimated repair time halved.`,
        actions: [],
        read: false,
      });
      return;
    }
    case 'override':
      state.score.overrideActive = action.active;
      return;
    case 'defer-maintenance': {
      const u = state.units[action.unit];
      if (!u || u.mode !== 'maintenance') return;
      u.mode = 'offline';
      u.maintenanceUntil = null;
      state.market.penalties += 40_000;
      state.market.penaltyByKind['maintenance-deferral'] =
        (state.market.penaltyByKind['maintenance-deferral'] ?? 0) + 40_000;
      return;
    }
    case 'checklist': {
      if (!state.score.checklist.includes(action.item)) state.score.checklist.push(action.item);
      return;
    }
    default:
      return;
  }
}
